/**
 * Browser audio capability probe (KT1 step 3; FR-71, NFR-01).
 *
 * Records what the browser claims to support. Claims are not proof — the
 * capture runs in capture.ts measure what is actually produced.
 */

export const RECORDER_MIME_CANDIDATES = [
  'audio/webm;codecs=opus',
  'audio/webm',
  'audio/mp4',
  'audio/mp4;codecs=mp4a.40.2',
  'audio/aac',
  'audio/ogg;codecs=opus',
  'audio/wav',
  'audio/mpeg',
] as const

const CONSTRAINT_KEYS = [
  'sampleRate',
  'sampleSize',
  'channelCount',
  'echoCancellation',
  'noiseSuppression',
  'autoGainControl',
] as const

export interface EnvironmentReport {
  probedAt: string
  userAgent: string
  secureContext: boolean
  standalone: boolean
  mediaDevices: boolean
  getUserMedia: boolean
  audioInputCount: number | null
  microphonePermission: string
  supportedConstraints: Record<string, boolean> | null
  mediaRecorder: boolean
  mediaRecorderTypes: Record<string, boolean> | null
  audioContext: boolean
  audioWorklet: boolean
  scriptProcessor: boolean
  defaultContextSampleRate: number | string | null
  context16kSampleRate: number | string | null
  audioSessionType: string | null
}

export function probeRecorderTypes(
  isTypeSupported: ((type: string) => boolean) | undefined,
): Record<string, boolean> | null {
  if (isTypeSupported === undefined) return null
  const result: Record<string, boolean> = {}
  for (const type of RECORDER_MIME_CANDIDATES) {
    try {
      result[type] = isTypeSupported(type)
    } catch {
      result[type] = false
    }
  }
  return result
}

export function pickConstraints(
  supported: Record<string, boolean | undefined>,
): Record<string, boolean> {
  const out: Record<string, boolean> = {}
  for (const key of CONSTRAINT_KEYS) out[key] = supported[key] === true
  return out
}

export function describeError(error: unknown): string {
  if (error instanceof Error) return `${error.name}: ${error.message}`
  return String(error)
}

export async function probeEnvironment(): Promise<EnvironmentReport> {
  const hasMediaDevices = 'mediaDevices' in navigator
  const hasAudioContext = 'AudioContext' in window
  const hasRecorder = 'MediaRecorder' in window

  return {
    probedAt: new Date().toISOString(),
    userAgent: navigator.userAgent,
    secureContext: window.isSecureContext,
    standalone: isStandalone(),
    mediaDevices: hasMediaDevices,
    getUserMedia: hasMediaDevices && 'getUserMedia' in navigator.mediaDevices,
    audioInputCount: hasMediaDevices ? await countAudioInputs() : null,
    microphonePermission: await microphonePermission(),
    supportedConstraints: hasMediaDevices
      ? pickConstraints(
          navigator.mediaDevices.getSupportedConstraints() as Record<
            string,
            boolean | undefined
          >,
        )
      : null,
    mediaRecorder: hasRecorder,
    mediaRecorderTypes: hasRecorder
      ? probeRecorderTypes((t) => MediaRecorder.isTypeSupported(t))
      : null,
    audioContext: hasAudioContext,
    audioWorklet: hasAudioContext && 'audioWorklet' in AudioContext.prototype,
    scriptProcessor: hasAudioContext && 'createScriptProcessor' in AudioContext.prototype,
    defaultContextSampleRate: hasAudioContext ? await contextSampleRate() : null,
    context16kSampleRate: hasAudioContext ? await contextSampleRate(16000) : null,
    audioSessionType: audioSessionType(),
  }
}

function isStandalone(): boolean {
  const nav = navigator as Navigator & { standalone?: boolean }
  return (
    window.matchMedia('(display-mode: standalone)').matches || nav.standalone === true
  )
}

async function countAudioInputs(): Promise<number | null> {
  try {
    const devices = await navigator.mediaDevices.enumerateDevices()
    return devices.filter((d) => d.kind === 'audioinput').length
  } catch {
    return null
  }
}

async function microphonePermission(): Promise<string> {
  if (!('permissions' in navigator)) return 'permissions-api-unavailable'
  try {
    const status = await navigator.permissions.query({ name: 'microphone' })
    return status.state
  } catch (error) {
    return `query-failed (${describeError(error)})`
  }
}

/** Constructs a throwaway context to read the rate the browser actually grants. */
async function contextSampleRate(requested?: number): Promise<number | string> {
  try {
    const ctx =
      requested === undefined
        ? new AudioContext()
        : new AudioContext({ sampleRate: requested })
    const rate = ctx.sampleRate
    await ctx.close()
    return rate
  } catch (error) {
    return `error (${describeError(error)})`
  }
}

function audioSessionType(): string | null {
  const nav = navigator as Navigator & { audioSession?: { type?: string } }
  return nav.audioSession?.type ?? null
}
